import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Form, Modal, Tabs, Tab, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getClientDetails,
    addClientProject,
    addClientInvoice,
    addClientActivity,
} from '../../service/apiService';

function ClientDetails() {
    const { clientId } = useParams();
    const navigate = useNavigate();

    const [client, setClient] = useState(null);
    const [projects, setProjects] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [activities, setActivities] = useState([]);
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [form, setForm] = useState({});

    const loadClient = async () => {
        const data = await getClientDetails(clientId);
        setClient(data.client);
        setProjects(data.projects);
        setInvoices(data.invoices);
        setActivities(data.activities);
    };

    useEffect(() => {
        loadClient();
    }, [clientId]);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleAddProject = async (e) => {
        e.preventDefault();
        const res = await addClientProject({ client_id: client.id, ...form });
        if (res.status === 'success') {
            alert('Project added');
            setShowProjectModal(false);
            loadClient();
        }
    };

    const handleAddInvoice = async (e) => {
        e.preventDefault();
        const res = await addClientInvoice({ client_id: client.id, ...form });
        if (res.status === 'success') {
            alert('Invoice added');
            setShowInvoiceModal(false);
            loadClient();
        }
    };

    const handleAddActivity = async (e) => {
        e.preventDefault();
        const res = await addClientActivity({ client_id: client.id, ...form });
        if (res.status === 'success') {
            alert('Activity logged');
            setShowActivityModal(false);
            loadClient();
        }
    };

    if (!client) return <p>Loading...</p>;

    return (
        <div className="client-details-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>{client.client_name}</h3>
                <Button variant="secondary" onClick={() => navigate(-1)}>← Back</Button>
            </div>

            <Card className="shadow-sm p-3 mb-3">
                <h5>Client Overview</h5>
                <p><strong>Type:</strong> {client.client_type.replace('_', ' ')}</p>
                <p><strong>Contact:</strong> {client.contact_person || '-'} | {client.phone || '-'} | {client.email}</p>
                <p><strong>Address:</strong> {client.address || '-'}, {client.city || '-'}</p>
                <p><strong>Notes:</strong> {client.notes || '-'}</p>
            </Card>

            <Tabs defaultActiveKey="projects" className="mb-3">
                {/* Projects */}
                <Tab eventKey="projects" title="Projects">
                    <div className="d-flex justify-content-end mb-3">
                        <Button size="sm" onClick={() => setShowProjectModal(true)}>+ Add Project</Button>
                    </div>
                    <Table bordered hover>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Budget</th>
                                <th>Start</th>
                                <th>End</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.length > 0 ? projects.map((p) => (
                                <tr key={p.id}>
                                    <td>{p.project_name}</td>
                                    <td><Badge bg={p.status === 'completed' ? 'success' : 'warning'}>{p.status}</Badge></td>
                                    <td>₹ {p.budget}</td>
                                    <td>{p.start_date || '-'}</td>
                                    <td>{p.end_date || '-'}</td>
                                </tr>
                            )) : <tr><td colSpan="5" className="text-center text-muted">No projects found</td></tr>}
                        </tbody>
                    </Table>
                </Tab>

                {/* Invoices */}
                <Tab eventKey="invoices" title="Invoices">
                    <div className="d-flex justify-content-end mb-3">
                        <Button size="sm" onClick={() => setShowInvoiceModal(true)}>+ Generate Invoice</Button>
                    </div>
                    <Table bordered hover>
                        <thead>
                            <tr>
                                <th>No</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.length > 0 ? (
                                invoices.map((inv) => (
                                    <tr key={inv.id}>
                                        <td>{inv.invoice_no}</td>
                                        <td>₹ {inv.amount}</td>
                                        <td>
                                            <Badge bg={inv.status === 'paid' ? 'success' : 'danger'}>
                                                {inv.status}
                                            </Badge>
                                        </td>
                                        <td>{inv.date}</td>
                                        <td>
                                            {inv.pdf_path ? (
                                                <a
                                                    href={`https://crm.semicoloninnovations.in/api/${inv.pdf_path}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Download
                                                </a>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center text-muted">
                                        No invoices found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                </Tab>

                {/* Activities */}
                <Tab eventKey="activities" title="Activities">
                    <div className="d-flex justify-content-end mb-3">
                        <Button size="sm" onClick={() => setShowActivityModal(true)}>+ Log Activity</Button>
                    </div>
                    <Table bordered hover>
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activities.length > 0 ? activities.map((a) => (
                                <tr key={a.id}>
                                    <td>{a.activity_type}</td>
                                    <td>{a.description}</td>
                                    <td>{new Date(a.created_on).toLocaleString()}</td>
                                </tr>
                            )) : <tr><td colSpan="3" className="text-center text-muted">No activities found</td></tr>}
                        </tbody>
                    </Table>
                </Tab>
            </Tabs>

            {/* Modals for adding new entries */}
            <Modal show={showProjectModal} onHide={() => setShowProjectModal(false)} centered>
                <Modal.Header closeButton><Modal.Title>Add Project</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddProject}>
                        <Form.Control name="project_name" placeholder="Project Name" onChange={handleChange} required className="mb-2" />
                        <Form.Control name="description" placeholder="Description" onChange={handleChange} className="mb-2" />
                        <Form.Control name="budget" placeholder="Budget" type="number" onChange={handleChange} className="mb-2" />
                        <Button type="submit" variant="primary">Save</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showInvoiceModal} onHide={() => setShowInvoiceModal(false)} centered>
                <Modal.Header closeButton><Modal.Title>Generate Invoice</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddInvoice}>
                        <Form.Control name="amount" placeholder="Amount" type="number" onChange={handleChange} required className="mb-2" />
                        <Form.Control name="description" placeholder="Description" onChange={handleChange} className="mb-2" />
                        <Button type="submit" variant="primary">Generate</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showActivityModal} onHide={() => setShowActivityModal(false)} centered>
                <Modal.Header closeButton><Modal.Title>Log Activity</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddActivity}>
                        <Form.Select name="activity_type" onChange={handleChange} className="mb-2">
                            <option value="call">Call</option>
                            <option value="meeting">Meeting</option>
                            <option value="email">Email</option>
                            <option value="followup">Follow-up</option>
                        </Form.Select>
                        <Form.Control name="description" placeholder="Description" as="textarea" onChange={handleChange} required className="mb-2" />
                        <Button type="submit" variant="primary">Save</Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default ClientDetails;
